import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLButtonElement} [buyNowButton]
 * @property {HTMLButtonElement} [minusButton]
 * @property {HTMLButtonElement} [plusButton]
 * @property {HTMLElement} quantityNumber
 */

/**
 * Sticky add-to-cart bar.
 * Desktop: shows when main buy buttons scroll out of view (original behaviour).
 * Mobile: reveals once after load settles, then never hides (avoids flicker).
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #mobileRevealed = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #buyNowLatched = false;

  /** @type {number | undefined} */
  #buyNowSyncTimeout;

  /** @type {number | undefined} */
  #openDrawerTimer;

  /** @type {number | undefined} */
  #mobileRevealTimeout;

  /** @type {MutationObserver | null} */
  #checkoutObserver = null;

  static #MOBILE_MQ = '(max-width: 1024px)';

  connectedCallback() {
    super.connectedCallback();

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (this.#isMobile()) {
      this.#setupMobileSticky();
    } else {
      this.#setupDesktopSticky(productForm);
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#initBuyNowSync(productForm);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#checkoutObserver?.disconnect();
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#buyNowSyncTimeout) clearTimeout(this.#buyNowSyncTimeout);
    if (this.#openDrawerTimer) clearTimeout(this.#openDrawerTimer);
    if (this.#mobileRevealTimeout) clearTimeout(this.#mobileRevealTimeout);
  }

  #isMobile() {
    return window.matchMedia(StickyAddToCartComponent.#MOBILE_MQ).matches;
  }

  /**
   * Mobile: stay hidden until load settles, then show once and never hide.
   */
  #setupMobileSticky() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';

    const reveal = () => {
      if (!this.isConnected || this.#mobileRevealed) return;
      this.#mobileRevealed = true;
      this.#showStickyBar();
    };

    const scheduleReveal = () => {
      if (this.#mobileRevealTimeout) clearTimeout(this.#mobileRevealTimeout);
      this.#mobileRevealTimeout = window.setTimeout(reveal, 400);
    };

    if (document.readyState === 'complete') {
      scheduleReveal();
    } else {
      this.#mobileRevealTimeout = window.setTimeout(reveal, 2500);
      window.addEventListener(
        'load',
        () => {
          scheduleReveal();
        },
        { once: true, signal: this.#abortController.signal }
      );
    }
  }

  /**
   * Desktop: show sticky only after main buy buttons have scrolled fully above the viewport.
   * Hidden at page top / while buy buttons are still visible or below the fold.
   * @param {Element | null} productForm
   */
  #setupDesktopSticky(productForm) {
    // Always start hidden on desktop — never show at page top.
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';

    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    const observeTarget =
      productForm.querySelector('.product-form-buttons') || buyButtonsBlock || productForm;
    if (!observeTarget) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Main buy buttons visible → sticky must stay hidden
      if (entry.isIntersecting) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
        return;
      }

      // Fully scrolled past main buy buttons → show sticky
      if (!this.#hiddenByBottom && this.#shouldShowStickyOnDesktop(entry.boundingClientRect)) {
        this.#showStickyBar();
        return;
      }

      // Still below the fold (not scrolled to yet) → keep hidden
      this.#hideStickyBar();
    });

    this.#buyButtonsIntersectionObserver.observe(observeTarget);
    this.#syncStickyVisibilityFromTarget(observeTarget);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          this.#hiddenByBottom = false;
          this.#syncStickyVisibilityFromTarget(observeTarget);
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#mainBottomObserver.observe(footer);
  }

  /**
   * True only when buy-button block has real size and has scrolled fully above the viewport.
   * @param {DOMRectReadOnly | DOMRect} rect
   */
  #shouldShowStickyOnDesktop(rect) {
    // Not laid out yet — never show sticky at top from a zero-size target.
    if (rect.height <= 0) return false;
    return rect.bottom <= 0;
  }

  /**
   * @param {Element} target
   */
  #syncStickyVisibilityFromTarget(target) {
    if (this.#isMobile()) {
      if (this.#mobileRevealed) this.#showStickyBar();
      return;
    }

    const rect = target.getBoundingClientRect();
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;

    // Desktop: hide while in view OR still below fold; show only after scrolled past.
    if (inView || !this.#shouldShowStickyOnDesktop(rect)) {
      this.#hideStickyBar();
    } else {
      this.#showStickyBar();
    }
  }

  /**
   * @param {Element | null} productForm
   */
  #initBuyNowSync(productForm) {
    this.#syncBuyNowVisibility();
    window.setTimeout(() => this.#syncBuyNowVisibility(), 500);
    window.setTimeout(() => this.#syncBuyNowVisibility(), 2000);

    if (!productForm) return;

    this.#checkoutObserver = new MutationObserver(() => this.#scheduleBuyNowSync());
    this.#checkoutObserver.observe(productForm, { childList: true, subtree: true });
    this.#abortController.signal.addEventListener('abort', () => this.#checkoutObserver?.disconnect());
  }

  handleAddToCartClick = async () => {
    const mainButton = this.#targetAddToCartButton;
    const stickyButton = this.refs.addToCartButton;
    if (!mainButton || !stickyButton) return;

    const mainAddToCartComponent = mainButton.closest('add-to-cart-component');

    mainButton.dataset.puppet = 'true';
    mainButton.click();

    await Promise.all([
      mainAddToCartComponent?.animateAddToCart
        ? mainAddToCartComponent.animateAddToCart()
        : this.#playAddToCartAnimation(mainButton),
      this.#playAddToCartAnimation(stickyButton),
    ]);

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      stickyButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * @param {HTMLButtonElement} button
   */
  #playAddToCartAnimation = async (button) => {
    if (button.dataset.added !== 'true') {
      button.dataset.added = 'true';
    }
    await onAnimationEnd(button);
  };

  handleBuyNowClick = () => {
    const stickyButton = this.refs.buyNowButton;
    const mainButton = this.#targetAddToCartButton;
    if (stickyButton?.disabled || !mainButton || mainButton.disabled) return;

    const drawer = document.getElementById('custom-cart-drawer');
    if (drawer) {
      drawer.setAttribute('data-open-after-add', 'true');
      if (this.#openDrawerTimer) clearTimeout(this.#openDrawerTimer);
      this.#openDrawerTimer = window.setTimeout(() => {
        drawer.removeAttribute('data-open-after-add');
        this.#openDrawerTimer = undefined;
      }, 8000);
    }

    mainButton.click();
  };

  handleQuantityDecrease = () => {
    this.#getMainQuantityButton('minus')?.click();
    this.#syncQuantityFromMain();
  };

  handleQuantityIncrease = () => {
    this.#getMainQuantityButton('plus')?.click();
    this.#syncQuantityFromMain();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.#isStuck = currentStuck === 'true';
    this.dataset.variantAvailable = variantAvailable;

    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#syncQuantityFromMain();

    const { buyNowButton } = this.refs;
    if (this.#buyNowLatched && buyNowButton instanceof HTMLButtonElement) {
      buyNowButton.hidden = false;
      buyNowButton.dataset.revealed = 'true';
    } else {
      this.#syncBuyNowVisibility();
    }
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
  };

  /**
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
      this.#resetTimeout = undefined;
    }

    this.refs.addToCartButton.removeAttribute('data-added');
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateQuantityDisplay();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    if (this.#isStuck && stickyBar.dataset.stuck === 'true') return;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    // Mobile: never hide after reveal
    if (this.#isMobile()) {
      if (this.#mobileRevealed) this.#showStickyBar();
      return;
    }

    const { stickyBar } = this.refs;
    if (!this.#isStuck && stickyBar.dataset.stuck === 'false') return;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * @param {'minus' | 'plus'} name
   * @returns {HTMLButtonElement | null}
   */
  #getMainQuantityButton(name) {
    const productForm = this.#getProductForm();
    if (!productForm) return null;
    return productForm.querySelector(`quantity-selector-component button[name="${name}"]`);
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getMainQuantityInput() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;
    return productForm.querySelector('quantity-selector-component input[name="quantity"]');
  }

  #getGokwikButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;
    const button = productForm.querySelector('#gokwik-buy-now, .gokwik-checkout button');
    return button instanceof HTMLButtonElement ? button : null;
  }

  #scheduleBuyNowSync() {
    if (this.#buyNowLatched) return;
    if (this.#buyNowSyncTimeout) clearTimeout(this.#buyNowSyncTimeout);
    this.#buyNowSyncTimeout = window.setTimeout(() => {
      this.#buyNowSyncTimeout = undefined;
      this.#syncBuyNowVisibility();
    }, 250);
  }

  #syncBuyNowVisibility() {
    const { buyNowButton } = this.refs;
    if (!(buyNowButton instanceof HTMLButtonElement)) return;

    if (this.#buyNowLatched) {
      buyNowButton.hidden = false;
      return;
    }

    const gokwikButton = this.#getGokwikButton();
    const style = gokwikButton ? window.getComputedStyle(gokwikButton) : null;
    const shouldShow = Boolean(
      gokwikButton &&
        !gokwikButton.disabled &&
        !gokwikButton.hidden &&
        gokwikButton.getAttribute('aria-hidden') !== 'true' &&
        style &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
    );

    if (!shouldShow) {
      buyNowButton.hidden = true;
      return;
    }

    this.#buyNowLatched = true;
    buyNowButton.hidden = false;
    buyNowButton.dataset.revealed = 'true';
    this.#checkoutObserver?.disconnect();
    this.#checkoutObserver = null;
  }

  #getInitialQuantity() {
    this.#syncQuantityFromMain();
  }

  #syncQuantityFromMain() {
    const quantityInput = this.#getMainQuantityInput();
    if (quantityInput) {
      this.#currentQuantity = parseInt(quantityInput.value, 10) || 1;
    } else {
      this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    }
    this.#updateQuantityDisplay();
  }

  #updateQuantityDisplay() {
    const { quantityNumber, minusButton, plusButton, addToCartButton, buyNowButton } = this.refs;
    quantityNumber.textContent = this.#currentQuantity.toString();

    const available = this.dataset.variantAvailable === 'true';
    const mainPlusButton = this.#getMainQuantityButton('plus');
    const mainMinusButton = this.#getMainQuantityButton('minus');

    const tagMax = Number(this.dataset.maxQty) || 0;
    const atTagMax = tagMax > 0 && this.#currentQuantity >= tagMax;
    if (minusButton) minusButton.disabled = !available || Boolean(mainMinusButton?.disabled);
    if (plusButton) plusButton.disabled = !available || Boolean(mainPlusButton?.disabled) || atTagMax;
    if (addToCartButton) addToCartButton.disabled = !available;
    if (buyNowButton) buyNowButton.disabled = !available;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
